/**
 * The calendar's colour rules, in one place so they can be checked without a
 * browser: what a seat's header looks like, what a visit booked into it looks
 * like, and which one wins.
 */

/**
 * Literal Tailwind classes per service color — Tailwind can't see dynamic
 * `bg-${color}-500/15` strings, so every supported color is spelled out.
 *
 * Must cover EVERY colour the picker offers (see Badge.tsx COLORS): a service
 * painted a colour that's missing here silently falls back to grey, which is
 * how eight differently-coloured drips once drew as one indistinguishable
 * block.
 */
export const COLOR_CLASSES: Record<string, string> = {
  violet: "bg-violet-500/15 border-l-violet-400 text-violet-100",
  purple: "bg-purple-500/15 border-l-purple-400 text-purple-100",
  fuchsia: "bg-fuchsia-500/15 border-l-fuchsia-400 text-fuchsia-100",
  pink: "bg-pink-500/15 border-l-pink-400 text-pink-100",
  rose: "bg-rose-500/15 border-l-rose-400 text-rose-100",
  red: "bg-red-500/15 border-l-red-400 text-red-100",
  orange: "bg-orange-500/15 border-l-orange-400 text-orange-100",
  amber: "bg-amber-500/15 border-l-amber-400 text-amber-100",
  yellow: "bg-yellow-500/15 border-l-yellow-400 text-yellow-100",
  green: "bg-green-500/15 border-l-green-400 text-green-100",
  emerald: "bg-emerald-500/15 border-l-emerald-400 text-emerald-100",
  teal: "bg-teal-500/15 border-l-teal-400 text-teal-100",
  cyan: "bg-cyan-500/15 border-l-cyan-400 text-cyan-100",
  sky: "bg-sky-500/15 border-l-sky-400 text-sky-100",
  blue: "bg-blue-500/15 border-l-blue-400 text-blue-100",
  indigo: "bg-indigo-500/15 border-l-indigo-400 text-indigo-100",
  slate: "bg-slate-500/15 border-l-slate-400 text-slate-100",
  zinc: "bg-zinc-500/15 border-l-zinc-400 text-zinc-100",
};

/**
 * The visit itself, painted in its COLUMN's colour.
 *
 * "make the colors match the chair colors so if I schedule in the sunroom the
 *  box is the same shade as the chair just darker" — the header wash is
 * /10, so the card is the same hue four times as deep. Same families as
 * TINT_CLASSES, or the two disagree about what a seat looks like.
 */
export const SEAT_CLASSES: Record<string, string> = {
  violet: "bg-violet-500/40 border-l-violet-300 text-violet-50",
  purple: "bg-purple-500/40 border-l-purple-300 text-purple-50",
  fuchsia: "bg-fuchsia-500/40 border-l-fuchsia-300 text-fuchsia-50",
  pink: "bg-pink-500/40 border-l-pink-300 text-pink-50",
  rose: "bg-rose-500/40 border-l-rose-300 text-rose-50",
  red: "bg-red-500/40 border-l-red-300 text-red-50",
  orange: "bg-orange-500/40 border-l-orange-300 text-orange-50",
  amber: "bg-amber-500/40 border-l-amber-300 text-amber-50",
  yellow: "bg-yellow-500/40 border-l-yellow-300 text-yellow-50",
  green: "bg-green-500/40 border-l-green-300 text-green-50",
  emerald: "bg-emerald-500/40 border-l-emerald-300 text-emerald-50",
  teal: "bg-teal-500/40 border-l-teal-300 text-teal-50",
  cyan: "bg-cyan-500/40 border-l-cyan-300 text-cyan-50",
  sky: "bg-sky-500/40 border-l-sky-300 text-sky-50",
  blue: "bg-blue-500/40 border-l-blue-300 text-blue-50",
  indigo: "bg-indigo-500/40 border-l-indigo-300 text-indigo-50",
  slate: "bg-slate-500/40 border-l-slate-300 text-slate-50",
  zinc: "bg-zinc-500/40 border-l-zinc-300 text-zinc-50",
};

/** The seat's colour if we know it, else the treatment's, else grey. */
export function visitColorClass(seatColor: string | null | undefined, serviceColor: string | null | undefined): string {
  return SEAT_CLASSES[seatColor ?? ""] ?? COLOR_CLASSES[serviceColor ?? ""] ?? COLOR_CLASSES.zinc!;
}

/**
 * Column header wash. Each seat carries its own colour (Settings → Menu &
 * seats) so the front desk can tell Chair 2 from Chair 3 at a glance without
 * reading the label — the same trick works for providers.
 */
export const TINT_CLASSES: Record<string, string> = {
  violet: "bg-violet-500/10",
  purple: "bg-purple-500/10",
  fuchsia: "bg-fuchsia-500/10",
  pink: "bg-pink-500/10",
  rose: "bg-rose-500/10",
  red: "bg-red-500/10",
  orange: "bg-orange-500/10",
  amber: "bg-amber-500/10",
  yellow: "bg-yellow-500/10",
  green: "bg-green-500/10",
  emerald: "bg-emerald-500/10",
  teal: "bg-teal-500/10",
  cyan: "bg-cyan-500/10",
  sky: "bg-sky-500/10",
  blue: "bg-blue-500/10",
  indigo: "bg-indigo-500/10",
  slate: "bg-slate-500/10",
  zinc: "bg-zinc-500/10",
};


